import { useEffect, useRef } from 'react';

interface ExtractSyncMessage {
    type: 'formType' | 'pageIndex';
    value: string | number;
    timestamp: number;
}

interface UseExtractSyncProps {
    selectedFormType: string;
    currentPageIndex: number;
    onFormTypeChange: (formType: string) => void;
    onPageIndexChange: (pageIndex: number) => void;
}

/**
 * Custom hook for synchronizing form type and page index across browser tabs
 * Uses BroadcastChannel API with localStorage fallback for older browsers
 */
export const useExtractSync = ({
    selectedFormType,
    currentPageIndex,
    onFormTypeChange,
    onPageIndexChange,
}: UseExtractSyncProps) => {
    const channelRef = useRef<BroadcastChannel | null>(null);
    const lastReceivedRef = useRef<{ formType?: number; pageIndex?: number }>({});
    const lastExternalPageIndexRef = useRef<number | null>(null);
    const lastExternalFormTypeRef = useRef<string | null>(null);

    // Initialize BroadcastChannel or localStorage listener
    useEffect(() => {
        const channelName = 'extract-data-sync';

        // Try to use BroadcastChannel if available
        if (typeof BroadcastChannel !== 'undefined') {
            channelRef.current = new BroadcastChannel(channelName);

            channelRef.current.onmessage = (event: MessageEvent<ExtractSyncMessage>) => {
                const { type, value, timestamp } = event.data;

                // Ignore messages we've already processed
                if (type === 'formType' && lastReceivedRef.current.formType === timestamp) {
                    return;
                }
                if (type === 'pageIndex' && lastReceivedRef.current.pageIndex === timestamp) {
                    return;
                }

                if (type === 'formType' && typeof value === 'string') {
                    lastReceivedRef.current.formType = timestamp;
                    lastExternalFormTypeRef.current = value;
                    onFormTypeChange(value);
                } else if (type === 'pageIndex' && typeof value === 'number') {
                    lastReceivedRef.current.pageIndex = timestamp;
                    lastExternalPageIndexRef.current = value;
                    onPageIndexChange(value);
                }
            };
        } else {
            // Fallback to localStorage
            const handleStorageChange = (e: StorageEvent) => {
                if (e.key === 'extract-sync' && e.newValue) {
                    try {
                        const data: ExtractSyncMessage = JSON.parse(e.newValue);
                        const { type, value, timestamp } = data;

                        // Ignore messages we've already processed
                        if (type === 'formType' && lastReceivedRef.current.formType === timestamp) {
                            return;
                        }
                        if (type === 'pageIndex' && lastReceivedRef.current.pageIndex === timestamp) {
                            return;
                        }

                        if (type === 'formType' && typeof value === 'string') {
                            lastReceivedRef.current.formType = timestamp;
                            lastExternalFormTypeRef.current = value;
                            onFormTypeChange(value);
                        } else if (type === 'pageIndex' && typeof value === 'number') {
                            lastReceivedRef.current.pageIndex = timestamp;
                            lastExternalPageIndexRef.current = value;
                            onPageIndexChange(value);
                        }
                    } catch (error) {
                        console.error('Failed to parse extract sync message:', error);
                    }
                }
            };

            window.addEventListener('storage', handleStorageChange);

            return () => {
                window.removeEventListener('storage', handleStorageChange);
            };
        }

        return () => {
            if (channelRef.current) {
                channelRef.current.close();
                channelRef.current = null;
            }
        };
    }, [onFormTypeChange, onPageIndexChange]);

    // Broadcast form type changes
    useEffect(() => {
        // Skip broadcasting if this value came from an external sync message
        if (lastExternalFormTypeRef.current === selectedFormType) {
            lastExternalFormTypeRef.current = null;
            return;
        }

        if (!selectedFormType) {
            return;
        }

        const message: ExtractSyncMessage = {
            type: 'formType',
            value: selectedFormType,
            timestamp: Date.now(),
        };

        if (channelRef.current) {
            channelRef.current.postMessage(message);
        } else {
            // Fallback to localStorage
            localStorage.setItem('extract-sync', JSON.stringify(message));
        }
    }, [selectedFormType]);

    // Broadcast page index changes
    useEffect(() => {
        // Skip broadcasting if this value came from an external sync message
        if (lastExternalPageIndexRef.current === currentPageIndex) {
            lastExternalPageIndexRef.current = null;
            return;
        }

        const message: ExtractSyncMessage = {
            type: 'pageIndex',
            value: currentPageIndex,
            timestamp: Date.now(),
        };

        if (channelRef.current) {
            channelRef.current.postMessage(message);
        } else {
            // Fallback to localStorage
            localStorage.setItem('extract-sync', JSON.stringify(message));
        }
    }, [currentPageIndex]);
};
